import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import {
  Entity,
  EntityDetails,
  FieldSet,
  Octopus,
} from '@maximizer/core/shared/domain';
import { ContextService } from './context.service';

@Injectable()
export class UdoService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  exists(udoName: string): Observable<boolean> {
    return this.http
      .post<Octopus.UdoDefinitionResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        {
          UdoDefinition: {
            Scope: {
              Fields: {
                Key: 1,
                Name: 1,
              },
            },
            Criteria: {
              SearchQuery: {
                Name: {
                  $EQ: udoName,
                },
              },
            },
          },
        },
      )
      .pipe(
        map((response) => {
          if (response.Code === Octopus.ResponseStatusCode.Successful) {
            const udoDefinitionData = response.UdoDefinition?.Data;
            return (
              Array.isArray(udoDefinitionData) && udoDefinitionData.length > 0
            );
          }
          return false;
        }),
      );
  }

  getDefinition(udoName: string): Observable<Octopus.UdoDefinition | null> {
    return this.http
      .post<Octopus.UdoDefinitionResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        {
          UdoDefinition: {
            Scope: {
              Fields: {
                Key: 1,
                Name: 1,
                DisplayName: 1,
              },
            },
            Criteria: {
              SearchQuery: {
                Name: {
                  $EQ: udoName,
                },
              },
            },
          },
        },
      )
      .pipe(
        map((response) => {
          if (response.Code === Octopus.ResponseStatusCode.Successful) {
            const udoDefinitionData = response.UdoDefinition?.Data;
            if (
              Array.isArray(udoDefinitionData) &&
              udoDefinitionData.length > 0
            ) {
              return udoDefinitionData[0];
            }
          }
          return null;
        }),
        catchError(() => of(null)),
      );
  }

  getEntityDetailsByKey(
    key: string,
    entityCode: Octopus.EntityCode,
    columns?: Octopus.Columns,
  ): Observable<EntityDetails> {
    const request: Octopus.EntityRequest = {
      [entityCode]: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: key,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            ParentKey: 1,
          },
          ...(columns && {
            Columns: columns,
          }),
        },
      },

      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.EntityResponseWithMetadata>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          const response =
            result[entityCode as keyof Octopus.EntityResponseWithMetadata];

          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            response?.Data?.[0]
          ) {
            const mapped = this.mapValueAndFields(entityCode, response.Data[0]);

            return {
              value: {
                ...mapped.value,
              },
              fields: {
                ...mapped.fields,
              },
            };
          }

          return { value: {}, fields: {} };
        }),
      );
  }

  private mapValueAndFields(
    entity: Octopus.EntityCode,
    value: Octopus.EntityWithMetadata,
  ): { value: Entity; fields: FieldSet } {
    const mapper = new Octopus.MetadataMapper(entity);
    const metadata = mapper.mapMetadata(value);
    const fields = mapper.mapFields(metadata);

    return { value, fields };
  }
}
